import { useEffect, useRef } from 'react';
import { MarkdownMessage } from './MarkdownMessage';
import type { ChatMessage } from '../../hooks/useAiChat';

interface Props {
  messages: ChatMessage[];
  streaming: boolean;
  activeTool: string | null;
  loading: boolean;
  examples: string[];
  onExample: (text: string) => void;
  documentNames: Map<string, string>;
}

/** Tool names are wire identifiers; this is what the owner should see. */
const TOOL_LABELS: Record<string, string> = {
  list_companies: 'companies',
  get_balance_sheet: 'balance sheet',
  get_income_statement: 'profit & loss',
  get_trial_balance: 'trial balance',
  list_accounts: 'chart of accounts',
  get_cash_flow: 'cash flow',
  get_general_ledger: 'general ledger',
  search_journal_entries: 'journal entries',
  list_bank_accounts: 'bank accounts',
  list_bank_transactions: 'bank feed',
  list_reconciliations: 'reconciliations',
  get_ar_aging: 'A/R aging',
  get_ap_aging: 'A/P aging',
  list_contacts: 'customers & vendors',
  get_books_health: 'books health',
  search_records: 'record search',
  search_documents: 'document vault',
  read_document: 'document',
};

export function toolLabel(name: string): string {
  return TOOL_LABELS[name] ?? name.replace(/_/g, ' ');
}

export function ChatTranscript({
  messages,
  streaming,
  activeTool,
  loading,
  examples,
  onExample,
  documentNames,
}: Props) {
  const endRef = useRef<HTMLDivElement>(null);

  // Follow the answer as it streams.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, streaming, activeTool]);

  const empty = messages.length === 0 && !streaming && !loading;

  return (
    <div
      className="card"
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {loading && <p className="muted">Loading conversation…</p>}

      {empty && (
        <div style={{ margin: 'auto', textAlign: 'center', maxWidth: 460 }}>
          <p className="muted" style={{ marginBottom: 12 }}>
            Try one of these:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {examples.map((ex) => (
              <button
                key={ex}
                type="button"
                className="btn-secondary"
                style={{ textAlign: 'left' }}
                onClick={() => onExample(ex)}
              >
                {ex}
              </button>
            ))}
          </div>
        </div>
      )}

      {messages.map((m) => (
        <div
          key={m.id}
          style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '80%' }}
        >
          {m.role === 'user' && m.attachedDocumentIds.length > 0 && (
            <div
              className="muted"
              style={{ fontSize: 11, marginBottom: 4, textAlign: 'right' }}
            >
              📎{' '}
              {m.attachedDocumentIds
                .map((id) => documentNames.get(id) ?? 'attached document')
                .join(', ')}
            </div>
          )}

          <div
            style={{
              background: m.role === 'user' ? 'var(--accent)' : 'var(--bg-surface-2)',
              color: m.role === 'user' ? '#fff' : 'var(--text-default)',
              padding: '8px 12px',
              borderRadius: 12,
              // Assistant replies go through the Markdown renderer, which sets
              // pre-wrap per paragraph. Applying it to the whole bubble would
              // add phantom blank lines around tables and lists.
              whiteSpace: m.role === 'user' ? 'pre-wrap' : 'normal',
              lineHeight: 1.5,
              minHeight: m.pending && !m.content ? 20 : undefined,
            }}
          >
            {m.role === 'assistant' ? (
              m.content ? (
                <MarkdownMessage text={m.content} />
              ) : (
                <span className="muted">Thinking…</span>
              )
            ) : (
              m.content
            )}
            {m.role === 'assistant' && m.pending && m.content && (
              <span aria-hidden style={{ opacity: 0.5 }}>▌</span>
            )}
          </div>

          {m.role === 'assistant' && m.toolsUsed.length > 0 && (
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              Looked up: {[...new Set(m.toolsUsed)].map(toolLabel).join(', ')}
            </div>
          )}
          {m.stopped && (
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
              Stopped
            </div>
          )}
        </div>
      ))}

      {activeTool && (
        <div className="muted" style={{ alignSelf: 'flex-start', fontSize: 12 }}>
          Looking up {toolLabel(activeTool)}…
        </div>
      )}

      <div ref={endRef} />
    </div>
  );
}
